import { ActionButton, Buttons, CancelButton, Icon, Level, ModalCard } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import { accountingProviders } from '~/lookups';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '~/styles';

const P = styled.p`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const WarningIcon = styled(Icon)`
  font-size: 2.5rem;
`;

const Warning = ({ children }) => {
  return (
    <Level margin="1rem 0">
      <Level.Item narrow>
        <WarningIcon icon="exclamation-triangle" color={colors.warning} />
      </Level.Item>
      <Level.Item>{children}</Level.Item>
    </Level>
  );
};

export default function SavePaymentToAccountingDialog({ payment, accountingProvider, resolve }) {
  const { workspace } = useWorkspace();
  const [{ isSubmitting, saved }, form] = useForm();
  const [accountingIntegration, setAccountingIntegration] = useState({ isReady: false, data: null, error: null });
  const toast = useToast();
  const api = useApi();

  const accountingProviderName = accountingProviders[accountingProvider].name;

  const validateAccountingIntegration = useCallback(async () => {
    if (!payment) return;

    try {
      const { data } = await api.www.workspaces(workspace.id).clientPayments(payment.id).accountingIntegration();

      if (!data || data.accountingProvider !== accountingProvider) {
        setAccountingIntegration({
          isReady: true,
          data: null,
          error: `This payment is not associated with a ${accountingProviderName} integration.`,
        });
        return;
      }

      setAccountingIntegration({ isReady: true, data, error: null });
    } catch (error) {
      setAccountingIntegration({
        isReady: true,
        data: null,
        error: error.message || 'Unable to validate accounting integration.',
      });
    }
  }, [api, workspace.id, payment, accountingProvider, accountingProviderName]);

  useEffect(() => {
    validateAccountingIntegration();
  }, [validateAccountingIntegration]);

  const handleSubmit = async () => {
    form.submit();

    try {
      await api.www.workspaces(workspace.id).clientPayments(payment.id)[`saveTo${accountingProviderName}`]();
      await resolve();
      toast.success(`Payment has been saved to ${accountingProviderName}.`);
    } catch (error) {
      toast.error(
        error.message ??
          `An error has occurred. The payment has not been saved to ${accountingProviderName}. Please review the ${accountingProviderName} integration settings and try again.`,
      );
      form.done();
    }
  };

  return (
    <ModalCard title={`Save to ${accountingProviderName}`} onClose={() => resolve(false)}>
      <ModalCard.Body>
        <P>{`Are you sure you want to save this payment to ${accountingIntegration.data?.accountingConnectionName || accountingProviderName}?`}</P>
        {accountingIntegration.error && <Warning>{accountingIntegration.error}</Warning>}
      </ModalCard.Body>

      <ModalCard.Footer>
        <Buttons align="right">
          <CancelButton onClick={() => resolve(false)}>Cancel</CancelButton>
          <ActionButton
            isLoading={isSubmitting}
            ok={saved}
            disabled={!!accountingIntegration.error}
            onClick={handleSubmit}>
            Save
          </ActionButton>
        </Buttons>
      </ModalCard.Footer>
    </ModalCard>
  );
}
